import { Stack } from '@mui/material';
import { useEffect } from 'react';
import { FormProvider, useForm } from 'react-hook-form';
import type { LopendeFase, Voordracht } from '../../api/typen.ts';
import { useVoordrachtMutaties } from '../../api/voordrachten.ts';
import { FormulierDialoog } from '../../componenten/FormulierDialoog.tsx';
import { Keuzeveld, Tekstveld } from '../../componenten/velden.tsx';
import { lopendeFaseLabels, opties } from '../../helpers/labels.ts';
import { useVerzenden } from '../../hooks/useVerzenden.ts';

interface Waarden {
  fase: LopendeFase | '';
  notitie: string;
}

interface Props {
  open: boolean;
  onSluit: () => void;
  voordracht: Voordracht;
  /** `fase` zet een lopende voordracht in een andere fase; `heropen` heropent een afgeronde voordracht. */
  modus: 'fase' | 'heropen';
}

/**
 * Zet een voordracht in een andere fase, of heropent een afgeronde voordracht in een fase.
 * @param props De voordracht en of het om een faseovergang of een heropening gaat.
 * @returns De dialoog.
 */
export function FaseDialoog({ open, onSluit, voordracht, modus }: Props) {
  const mutaties = useVoordrachtMutaties();
  const faseOpties = opties(lopendeFaseLabels).filter(
    (optie) => modus === 'heropen' || optie.waarde !== voordracht.fase,
  );
  const formulier = useForm<Waarden>({ defaultValues: { fase: '', notitie: '' } });
  useEffect(() => {
    if (open) {
      formulier.reset({ fase: '', notitie: '' });
    }
  }, [open, formulier]);

  const { onVerzend, fout, bezig } = useVerzenden(
    formulier,
    (waarden) => {
      if (!waarden.fase) {
        throw new Error('Kies een fase.');
      }
      const invoer = { fase: waarden.fase, notitie: waarden.notitie || null };
      return modus === 'fase'
        ? mutaties.naarFase.mutateAsync({ id: voordracht.id, invoer })
        : mutaties.heropen.mutateAsync({ id: voordracht.id, invoer });
    },
    { melding: modus === 'fase' ? 'Fase gewijzigd' : 'Voordracht heropend', naSucces: onSluit },
  );

  return (
    <FormProvider {...formulier}>
      <FormulierDialoog
        open={open}
        titel={modus === 'fase' ? 'Naar een andere fase' : 'Voordracht heropenen'}
        onSluit={onSluit}
        onVerzend={onVerzend}
        fout={fout}
        bezig={bezig}
        verzendLabel={modus === 'fase' ? 'Fase wijzigen' : 'Heropenen'}
        maxWidth="xs"
      >
        <Stack spacing={2}>
          <Keuzeveld naam="fase" label="Fase" opties={faseOpties} verplicht autoFocus />
          <Tekstveld naam="notitie" label="Notitie" meerregelig rijen={3} />
        </Stack>
      </FormulierDialoog>
    </FormProvider>
  );
}
